import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Search, Download, Award, GraduationCap, X, Mail, MailCheck, MailWarning, Send, Eye, Trash2, AlertTriangle, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { previewStore } from "../academy-portal/previewStore";
import AdminLiveClasses from "./AdminLiveClasses";

const TEST_EMAIL_RE = /^(test_|TEST_)/;
const isTestAccount = (l) => TEST_EMAIL_RE.test(l?.email || "") || TEST_EMAIL_RE.test(l?.first_name || "") || TEST_EMAIL_RE.test(l?.last_name || "");

export default function AdminAcademyPortal() {
  const { refreshOverview } = useOutletContext();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [learners, setLearners] = useState([]);
  const [q, setQ] = useState(""); const [statusFilter, setStatusFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("CTA-001");
  const [selected, setSelected] = useState(null); const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingPreview, setOpeningPreview] = useState(false);
  const [showTestOnly, setShowTestOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirming, setConfirming] = useState(null); // { learners, includeCompleted, typed }

  const openPreview = useCallback(async () => {
    if (openingPreview) return;
    setOpeningPreview(true);
    let code = "CTA-001";
    let target = "/academy/portal/dashboard";
    if (courseFilter === "HOSP-001") { code = "HOSP-001"; target = "/academy/portal/hosp-001/learn"; }
    else if (courseFilter === "PVC-001") { code = "PVC-001"; target = "/academy/portal/pvc-001/learn"; }
    else if (courseFilter === "HOSP-101") { code = "HOSP-101"; target = "/academy/portal/hosp-101/learn"; }
    else if (courseFilter === "FD2G-FAC-001") { code = "FD2G-FAC-001"; target = "/academy/portal/fd2g-fac-001/learn"; }
    try {
      // Server-side sanity check that admin is still authenticated before we flip the flag.
      await api.get("/admin/portal/preview/course", { params: { code } });
      previewStore.enter(code);
      nav(target);
    } catch (err) {
      toast.error("Could not start preview. Please sign in as an admin and try again.");
    } finally {
      setOpeningPreview(false);
    }
  }, [nav, openingPreview, courseFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, lr] = await Promise.all([
        api.get("/admin/portal/overview"),
        api.get("/admin/portal/learners", { params: { q: q.trim() || undefined, status_filter: statusFilter || undefined, course_code: courseFilter || undefined } })
      ]);
      setOverview(ov.data); setLearners(lr.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  }, [q, statusFilter, courseFilter]);

  useEffect(() => { load(); }, [load]);

  const visibleLearners = useMemo(
    () => (showTestOnly ? learners.filter(isTestAccount) : learners),
    [learners, showTestOnly],
  );

  const visibleIds = useMemo(() => new Set(visibleLearners.map(l => l.id)), [visibleLearners]);
  const selectedVisible = useMemo(() => visibleLearners.filter(l => selectedIds.has(l.id)), [visibleLearners, selectedIds]);
  const allVisibleSelected = visibleLearners.length > 0 && visibleLearners.every(l => selectedIds.has(l.id));

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (allVisibleSelected) visibleLearners.forEach(l => n.delete(l.id));
      else visibleLearners.forEach(l => n.add(l.id));
      return n;
    });
  };

  // Prune selection when filter or fetched list changes so we never carry hidden IDs.
  useEffect(() => {
    setSelectedIds(prev => {
      const n = new Set(); prev.forEach(id => { if (visibleIds.has(id)) n.add(id); });
      return n;
    });
  }, [visibleIds]);

  const startBulkDelete = () => {
    if (selectedVisible.length === 0) return;
    const anyCompleted = selectedVisible.some(l => l.enrollment?.status === "completed" || l.certificate_number);
    setConfirming({ learners: selectedVisible, includeCompleted: false, typed: "", anyCompleted });
  };

  const startSingleDelete = (learner) => {
    const anyCompleted = !!(learner.enrollment?.status === "completed" || learner.certificate_number);
    setConfirming({ learners: [learner], includeCompleted: false, typed: "", anyCompleted, single: true });
  };

  const executeDelete = async () => {
    if (!confirming) return;
    if (confirming.typed !== "DELETE") return;
    const ids = confirming.learners.map(l => l.id);
    const includeCompleted = confirming.anyCompleted ? confirming.includeCompleted : false;
    try {
      const r = await api.post("/admin/portal/learners/bulk-delete", {
        learner_ids: ids, confirm: "DELETE", include_completed: includeCompleted,
      });
      toast.success(`Deleted ${r.data.deleted} learner${r.data.deleted === 1 ? "" : "s"}.`);
      setConfirming(null);
      setSelectedIds(new Set());
      if (selected && ids.includes(selected.id)) { setSelected(null); setDetail(null); }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openDetail = async (l) => {
    setSelected(l); setDetail(null);
    try { const r = await api.get(`/admin/portal/learners/${l.id}`); setDetail(r.data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const downloadCert = async (certId, certNumber) => {
    try {
      const r = await api.get(`/admin/portal/certificates/${certId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `CCCHAPS-${certNumber}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const [sendingReminder, setSendingReminder] = useState(false);
  const sendReminder = async (learnerId) => {
    if (sendingReminder) return;
    setSendingReminder(true);
    try {
      const r = await api.post(`/admin/portal/learners/${learnerId}/send-reminder`);
      const s = r.data?.status;
      if (s === "sent") toast.success("Reminder email sent.");
      else if (s === "skipped_no_key") toast.info("Reminder queued (Resend API key not configured in this environment).");
      else if (s === "failed") toast.error("Reminder logged as failed — check email log.");
      else toast.success("Reminder recorded.");
      // Refresh detail + list
      if (selected) { const r2 = await api.get(`/admin/portal/learners/${learnerId}`); setDetail(r2.data); }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSendingReminder(false);
    }
  };

  return (
    <div data-testid="admin-academy-portal">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[#4A6FA5] mb-1"><GraduationCap className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">CCCHAPS Academy Portal</span></div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Learners & Completions</h2>
          <p className="mt-1 text-sm text-slate-500">
            {courseFilter === "CTA-001" && "CTA-001 — Grief in the Workplace"}
            {courseFilter === "HOSP-001" && "HOSP-001 — See the Person First · Track Three Preview"}
            {courseFilter === "PVC-001" && "PVC-001 — The Patient's Voice in Care · In Development"}
            {courseFilter === "HOSP-101" && "HOSP-101 — Practicing From Diagnosis to Goodbye · Admin Preview"}
            {courseFilter === "FD2G-FAC-001" && "FD2G-FAC-001 — The Five Voices of the Journey — Certified Facilitator Training · Admin Preview"}
            {courseFilter === "ALL" && "All courses · CTA-001 + HOSP-001 + PVC-001 + HOSP-101 + FD2G-FAC-001"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={courseFilter} onChange={e=>setCourseFilter(e.target.value)} data-testid="admin-course-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
            <option value="CTA-001">CTA-001 — Grief in the Workplace</option>
            <option value="HOSP-001">HOSP-001 — See the Person First</option>
            <option value="PVC-001">PVC-001 — The Patient's Voice in Care (In Development)</option>
            <option value="HOSP-101">HOSP-101 — Practicing From Diagnosis to Goodbye (Admin Preview)</option>
            <option value="FD2G-FAC-001">FD2G-FAC-001 — The Five Voices of the Journey — Certified Facilitator Training (Admin Preview)</option>
            <option value="ALL">All Courses</option>
          </select>
          <button
            onClick={openPreview}
            disabled={openingPreview || courseFilter === "ALL"}
            data-testid="admin-preview-as-learner"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
            title={courseFilter === "ALL" ? "Select a specific course to preview." : `Experience ${courseFilter} as a learner. No production data is created.`}
          >
            <Eye className="w-4 h-4"/> {openingPreview ? "Opening…" : `PREVIEW AS LEARNER (${courseFilter === "ALL" ? "CTA-001" : courseFilter})`}
          </button>
          <button onClick={load} data-testid="portal-refresh" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5]"><RefreshCw className="w-4 h-4"/> Refresh</button>
        </div>
      </div>

      <TestEmailPanel />

      {overview && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="portal-overview">
          <StatCard label="Total learners" value={overview.total_learners}/>
          <StatCard label="Active" value={overview.active_learners}/>
          <StatCard label="Completed" value={overview.completed_learners}/>
          <StatCard label="Certificates issued" value={overview.certificates_issued}/>
          <StatCard label="Avg. knowledge check (passed)" value={overview.average_quiz_score ?? "—"}/>
        </div>
      )}

      {overview?.courses_summary && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="portal-courses-summary">
          {overview.courses_summary.map(cs => {
            const meta = cs.code === "HOSP-001" ? { label: "Track Three Preview", title: "See the Person First" }
                       : cs.code === "PVC-001" ? { label: "In Development", title: "The Patient's Voice in Care" }
                       : cs.code === "HOSP-101" ? { label: "Admin Preview", title: "Practicing From Diagnosis to Goodbye" }
                       : cs.code === "FD2G-FAC-001" ? { label: "Admin Preview", title: "The Five Voices of the Journey" }
                       : { label: "Workplace Grief", title: "Grief in the Workplace" };
            return (
            <div key={cs.code} className={`rounded-xl border p-4 ${courseFilter === cs.code ? "border-[#4A6FA5] bg-[#F0F4F8]" : "border-slate-200 bg-white"}`} data-testid={`portal-course-summary-${cs.code}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]">{cs.code}</p>
                <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${cs.code === "PVC-001" || cs.code === "HOSP-101" || cs.code === "FD2G-FAC-001" ? "text-amber-700" : "text-slate-500"}`}>{meta.label}</span>
              </div>
              <p className="mt-1 text-sm font-medium text-slate-900">{meta.title}</p>
              <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div><dt className="text-slate-500">Enrolled</dt><dd className="font-semibold text-slate-900">{cs.enrolled}</dd></div>
                <div><dt className="text-slate-500">In progress</dt><dd className="font-semibold text-slate-900">{cs.in_progress}</dd></div>
                <div><dt className="text-slate-500">Completed</dt><dd className="font-semibold text-slate-900">{cs.completed}</dd></div>
              </dl>
              <p className="mt-2 text-[11px] text-slate-500">Completion rate: <strong className="text-slate-800">{cs.completion_rate}%</strong></p>
              {cs.code === "PVC-001" && (
                <p className="mt-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-amber-700">Not yet enrolling</p>
              )}
            </div>
            );
          })}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} data-testid="portal-search" placeholder="Search name, email, organization…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/>
        </div>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} data-testid="portal-status-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option><option value="in_progress">In progress</option><option value="completed">Completed</option>
        </select>
        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="portal-filter-test">
          <input type="checkbox" checked={showTestOnly} onChange={e=>setShowTestOnly(e.target.checked)} className="w-4 h-4 accent-[#4A6FA5]"/>
          <span className="uppercase tracking-[0.12em] font-semibold">Test accounts</span>
        </label>
      </div>

      {selectedVisible.length > 0 && (
        <div data-testid="portal-bulk-toolbar" className="mb-3 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-rose-900">
            <strong>{selectedVisible.length}</strong> learner{selectedVisible.length === 1 ? "" : "s"} selected.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-md border border-slate-300">Clear</button>
            <button onClick={startBulkDelete} data-testid="portal-bulk-delete" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold">
              <Trash2 className="w-3.5 h-3.5"/> Delete Selected Learners
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-12 text-center text-slate-500 text-sm">Loading…</div> :
          visibleLearners.length === 0 ? <div className="p-12 text-center text-slate-500 text-sm">No learners{showTestOnly ? " match TEST_ prefix" : " yet"}.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} data-testid="portal-select-all" aria-label="Select all visible" className="w-4 h-4 accent-[#4A6FA5]"/>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Organization</th>
                  <th className="text-left px-4 py-3 font-semibold">Enrolled</th>
                  <th className="text-left px-4 py-3 font-semibold">Progress</th>
                  <th className="text-left px-4 py-3 font-semibold">Score</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Certificate</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleLearners.map(l => {
                  const isTest = isTestAccount(l);
                  const isChecked = selectedIds.has(l.id);
                  return (
                  <tr key={l.id} className={`hover:bg-slate-50/60 ${isChecked ? "bg-rose-50/40" : ""}`} data-testid={`portal-row-${l.id}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={isChecked} onChange={() => toggleOne(l.id)} data-testid={`portal-select-${l.id}`} aria-label={`Select ${l.first_name} ${l.last_name}`} className="w-4 h-4 accent-[#4A6FA5]"/>
                    </td>
                    <td className="px-4 py-3 text-slate-900 font-medium">
                      {l.first_name} {l.last_name}
                      {isTest && <span data-testid={`portal-test-badge-${l.id}`} className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.14em] bg-amber-100 text-amber-800 border border-amber-200 uppercase">TEST</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{l.email}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{l.organization || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{l.enrollment ? new Date(l.enrollment.enrolled_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#4A6FA5]" style={{width:`${l.progress_pct}%`}}/></div><span className="text-xs text-slate-600">{l.progress_pct}%</span></div></td>
                    <td className="px-4 py-3 text-xs">{l.best_score ?? "—"}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded text-xs ${l.enrollment?.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{l.enrollment?.status?.replace("_", " ") || "—"}</span></td>
                    <td className="px-4 py-3 text-xs font-mono">{l.certificate_number || "—"}</td>
                    <td className="px-4 py-3 text-right"><button onClick={()=>openDetail(l)} data-testid={`portal-view-${l.id}`} className="text-[#4A6FA5] hover:text-[#3B597B] text-sm font-medium">View</button></td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        }
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="portal-drawer">
          <div className="flex-1 bg-slate-900/30" onClick={()=>setSelected(null)}/>
          <div className="w-full max-w-md bg-white border-l border-slate-200 overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Learner</p>
                <h3 className="font-heading text-xl text-slate-900">{selected.first_name} {selected.last_name}</h3>
                <p className="mt-1 text-sm text-slate-600">{selected.email}</p>
              </div>
              <button onClick={()=>setSelected(null)} data-testid="portal-drawer-close" className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4"/></button>
            </div>
            {!detail ? <p className="text-sm text-slate-500">Loading…</p> : (
              <div className="space-y-4 text-sm">
                <Row label="Organization" v={detail.learner.organization}/>
                <Row label="Chaplain role" v={detail.learner.chaplain_role}/>
                <Row label="Accommodation" v={detail.learner.accommodation_request} pre/>
                <Row label="Marketing opt-in" v={detail.learner.marketing_optin ? "Yes" : "No"}/>
                <Row label="Account created" v={new Date(detail.learner.created_at).toLocaleString()}/>
                {detail.enrollment && <>
                  <Row label="Enrolled" v={new Date(detail.enrollment.enrolled_at).toLocaleString()}/>
                  <Row label="Enrollment status" v={detail.enrollment.status}/>
                  {detail.enrollment.completed_at && <Row label="Completed at" v={new Date(detail.enrollment.completed_at).toLocaleString()}/>}
                </>}
                <Row label="Module completions" v={detail.progress ? Object.keys(detail.progress.section_completions || {}).length : 0}/>
                <Row label="Case study" v={detail.progress?.case_study_completed ? "Complete" : "Pending"}/>
                <Row label="Evaluation" v={detail.evaluation ? "Submitted" : "Pending"}/>
                {detail.quiz_attempts && detail.quiz_attempts.length > 0 && (
                  <div>
                    <dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Knowledge check attempts</dt>
                    <ul className="mt-1 space-y-1">
                      {detail.quiz_attempts.map(a => (
                        <li key={a.id} className="text-xs text-slate-700">Attempt {a.attempt_number}: {a.score}/{a.total} {a.passed && <span className="text-emerald-700 font-semibold">· Passed</span>} <span className="text-slate-400">· {new Date(a.submitted_at).toLocaleString()}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.certificate && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-emerald-800 font-semibold">Certificate</p>
                    <p className="mt-1 font-mono text-sm text-emerald-900">{detail.certificate.cert_number}</p>
                    <button onClick={()=>downloadCert(detail.certificate.id, detail.certificate.cert_number)} data-testid="admin-download-cert" className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-800"><Download className="w-3.5 h-3.5"/> Download PDF</button>
                  </div>
                )}
                {detail.evaluation && (
                  <div>
                    <dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Evaluation</dt>
                    <p className="mt-1 text-xs text-slate-700">Overall {detail.evaluation.overall_rating}/5 · Clarity {detail.evaluation.clarity_rating}/5 · Understanding {detail.evaluation.understanding_rating}/5</p>
                    {detail.evaluation.most_useful && <p className="mt-1 text-xs text-slate-600"><em>Most useful:</em> {detail.evaluation.most_useful}</p>}
                    {detail.evaluation.could_improve && <p className="mt-1 text-xs text-slate-600"><em>Could improve:</em> {detail.evaluation.could_improve}</p>}
                    {detail.evaluation.testimonial_optin && <p className="mt-1 text-xs text-emerald-700 font-semibold">Testimonial opt-in ✓</p>}
                  </div>
                )}

                <div data-testid="portal-email-log">
                  <div className="flex items-center justify-between mb-2">
                    <dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Email log</dt>
                    {detail.enrollment && detail.enrollment.status !== "completed" && (
                      <button
                        onClick={() => sendReminder(selected.id)}
                        disabled={sendingReminder}
                        data-testid="portal-send-reminder"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4A6FA5] text-white text-xs font-medium hover:bg-[#3B597B] disabled:opacity-60"
                      ><Send className="w-3 h-3" /> {sendingReminder ? "Sending…" : "Send reminder"}</button>
                    )}
                  </div>
                  {(!detail.emails || detail.emails.length === 0) ? (
                    <p className="text-xs text-slate-500">No emails logged yet.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {detail.emails.map((e) => (
                        <li key={e.id} className="flex items-start gap-2 text-xs">
                          <EmailStatusIcon status={e.status} />
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-800 truncate">
                              <span className="font-medium">{EMAIL_EVENT_LABELS[e.event_type] || e.event_type}</span>
                              <span className="ml-2 text-slate-400">· {new Date(e.created_at).toLocaleString()}</span>
                            </div>
                            <div className="text-slate-500 truncate">{e.subject}</div>
                            <div className="text-[10px] text-slate-400">
                              status: <span className={`font-semibold ${statusColor(e.status)}`}>{e.status}</span>
                              {e.provider_message_id && <span className="ml-2">· provider id: <span className="font-mono text-slate-500">{e.provider_message_id}</span></span>}
                              {!e.provider_message_id && e.status === "sent" && <span className="ml-2 text-amber-700">· no provider id captured</span>}
                              {e.error && <span className="ml-2 text-rose-600">error: {e.error}</span>}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-xs uppercase tracking-[0.16em] text-rose-700 font-semibold mb-2">Danger zone</p>
                  <button
                    onClick={() => startSingleDelete(selected)}
                    data-testid="portal-drawer-delete"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-rose-300 bg-white text-rose-700 text-sm font-semibold hover:bg-rose-50"
                  >
                    <Trash2 className="w-4 h-4" /> Delete Learner
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <AdminLiveClasses />

      {confirming && <DeleteConfirmModal state={confirming} setState={setConfirming} onConfirm={executeDelete} />}
    </div>
  );
}

function DeleteConfirmModal({ state, setState, onConfirm }) {
  const { learners, includeCompleted, typed, anyCompleted, single } = state;
  const disabled = typed !== "DELETE" || (anyCompleted && !includeCompleted);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60" data-testid="portal-delete-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-4 bg-rose-700 text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="font-heading text-lg font-semibold">Permanently delete learner{learners.length === 1 ? "" : "s"}</h3>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm">
          <p>
            You are about to <strong>permanently delete {learners.length} learner account{learners.length === 1 ? "" : "s"}</strong>.
            This also removes their enrollment, progress, quiz attempts, evaluation, certificate record,
            Academy email log entries, live-class registrations, and attendance history.
            The CTA-001 course and other learners are not affected.
          </p>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 max-h-40 overflow-y-auto" data-testid="portal-delete-list">
            <ul className="text-xs space-y-1">
              {learners.map(l => (
                <li key={l.id} className="flex items-start justify-between gap-3">
                  <span><strong>{l.first_name} {l.last_name}</strong> <span className="text-slate-500">— {l.email}</span></span>
                  {(l.enrollment?.status === "completed" || l.certificate_number) && (
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">completed</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {anyCompleted && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="portal-delete-completed-warning">
              <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0"/>
              <div className="text-xs text-amber-900">
                <p className="font-semibold mb-1">One or more selected learners have a completed course record and/or issued certificate.</p>
                <p>Permanent deletion will remove that completion record and certificate. Confirm this second time to allow it.</p>
                <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={includeCompleted} onChange={e => setState({ ...state, includeCompleted: e.target.checked })} data-testid="portal-delete-completed-ack" className="w-4 h-4 accent-amber-700"/>
                  <span className="text-xs font-semibold">Yes, also remove completion + certificate records.</span>
                </label>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Type <code className="text-rose-700">DELETE</code> to confirm</label>
            <input
              value={typed}
              onChange={e => setState({ ...state, typed: e.target.value })}
              data-testid="portal-delete-confirm-input"
              autoFocus
              autoComplete="off"
              className="w-full rounded-lg border-2 border-slate-300 focus:border-rose-500 px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none"
              placeholder="DELETE"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex flex-wrap justify-end gap-2 bg-slate-50">
          <button onClick={() => setState(null)} data-testid="portal-delete-cancel" className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-white">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            data-testid="portal-delete-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-700 text-white text-sm font-bold hover:bg-rose-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4"/> PERMANENTLY DELETE
          </button>
        </div>
      </div>
    </div>
  );
}

const EMAIL_EVENT_LABELS = {
  registration: "Registration confirmation",
  incomplete_reminder: "Incomplete-course reminder",
  completion: "Course completion",
  zoom_registration: "Live session registration",
  class_reminder_24h: "24-hour class reminder",
};

function statusColor(s) {
  if (s === "sent") return "text-emerald-700";
  if (s === "skipped_no_key") return "text-slate-500";
  if (s === "failed") return "text-rose-700";
  return "text-slate-600";
}

function EmailStatusIcon({ status }) {
  if (status === "sent") return <MailCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />;
  if (status === "failed") return <MailWarning className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />;
  return <Mail className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />;
}

function StatCard({ label, value }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-4"><p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</p><p className="mt-1 font-heading text-2xl text-slate-900">{value}</p></div>;
}
function Row({ label, v, pre }) { if (!v && v !== 0 && v !== false) return null; return <div><dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</dt><dd className={`text-slate-800 mt-0.5 ${pre ? "whitespace-pre-wrap" : ""}`}>{String(v)}</dd></div>; }


function TestEmailPanel() {
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { status, provider_message_id, error, to, sender_email }
  const [config, setConfig] = useState(null);

  // Load the diagnostic config once so the sender email is visible on the panel.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/email-diagnostic/config");
        if (alive) setConfig(data);
      } catch {
        /* silent — panel still works without config preview */
      }
    })();
    return () => { alive = false; };
  }, []);

  const canSend = /^\S+@\S+\.\S+$/.test(to.trim()) && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email-diagnostic/send", { to: to.trim() });
      setResult({
        status: data?.status || "unknown",
        provider_message_id: data?.provider_message_id || null,
        error: data?.error || null,
        to: to.trim(),
        sender_email: config?.sender_email || null,
      });
      if (data?.status === "sent") toast.success("Test email sent.");
      else if (data?.status === "skipped_no_key") toast.info("Skipped — no Resend API key configured in this environment.");
      else toast.error("Send failed — see details below.");
    } catch (err) {
      setResult({
        status: "failed",
        provider_message_id: null,
        error: formatApiErrorDetail(err.response?.data?.detail) || err.message,
        to: to.trim(),
        sender_email: config?.sender_email || null,
      });
      toast.error("Send failed.");
    } finally {
      setSending(false);
    }
  };

  const statusPillClass = (s) =>
    s === "sent" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : s === "skipped_no_key" ? "bg-slate-100 text-slate-700 border-slate-200"
    : s === "failed" ? "bg-rose-50 text-rose-800 border-rose-200"
    : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div
      data-testid="admin-test-email-panel"
      className="bg-white border border-slate-200 rounded-xl p-4 mb-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <MailCheck className="w-4 h-4 text-[#4A6FA5]"/>
        <h3 className="font-heading text-sm tracking-tight text-slate-900">Send Academy Test Email</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Sends a one-off diagnostic email through the current Resend configuration. Does not touch learners, courses, or enrollments.
        {config?.sender_email && <> Sender: <span className="font-mono text-slate-700">{config.sender_email}</span>.</>}
      </p>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@example.com"
          data-testid="test-email-recipient"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"
          disabled={sending}
        />
        <button
          onClick={send}
          disabled={!canSend}
          data-testid="test-email-send"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3d5c8a] disabled:opacity-60"
        >
          <Send className="w-4 h-4"/> {sending ? "Sending…" : "Send test email"}
        </button>
      </div>

      {result && (
        <div
          data-testid="test-email-result"
          className={`mt-3 rounded-lg border p-3 text-sm ${statusPillClass(result.status)}`}
        >
          <div className="flex items-center gap-2 font-semibold">
            {result.status === "sent" && <MailCheck className="w-4 h-4"/>}
            {result.status === "failed" && <MailWarning className="w-4 h-4"/>}
            {result.status === "skipped_no_key" && <Mail className="w-4 h-4"/>}
            <span data-testid="test-email-status">Status: {result.status}</span>
          </div>
          <dl className="mt-2 grid grid-cols-1 gap-1.5 text-xs">
            {result.sender_email && (
              <div><dt className="uppercase tracking-[0.14em] text-[10px] opacity-70">Sender</dt>
              <dd className="font-mono">{result.sender_email}</dd></div>
            )}
            <div><dt className="uppercase tracking-[0.14em] text-[10px] opacity-70">Recipient</dt>
            <dd className="font-mono" data-testid="test-email-recipient-echo">{result.to}</dd></div>
            {result.provider_message_id && (
              <div>
                <dt className="uppercase tracking-[0.14em] text-[10px] opacity-70">Provider message ID</dt>
                <dd className="font-mono break-all" data-testid="test-email-message-id">{result.provider_message_id}</dd>
              </div>
            )}
            {result.error && (
              <div>
                <dt className="uppercase tracking-[0.14em] text-[10px] opacity-70">Error</dt>
                <dd className="font-mono break-all whitespace-pre-wrap" data-testid="test-email-error">{result.error}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}
